---
title: Podpora a řešení problémů
sidebar_position: 7
description: Podpora a řešení problémů
---

# Podpora a řešení problémů

## Časté problémy a řešení

#### Problém: Prázdný widget pozorování

**Příznaky**:

- V widgetu pozorování se nezobrazují žádné satelitní snímky
- Zpráva: „Žádná pozorování nejsou dostupná"

**Možné příčiny a řešení**:

1. **Časové období neodpovídá dostupnosti dat**:
   - **Řešení**: Otevřete **boční panel** a přejděte do **Nastavení**, kde upravte časové období
   - Zkontrolujte, zda časové rozmezí zahrnuje období, ve kterých byla sbírána satelitní data
   - Zkuste rozšířit rozsah dat nebo zvolit jiné období

2. **Vybraná vrstva není dostupná**:
   - **Řešení**: V **panelu pozorování** přepněte na jinou vrstvu pozorování
   - Některé vrstvy nemusí být dostupné pro všechna časová období

3. **Chyba při zpracování satelitních snímků**:
   - **Příznak**: Namísto snímku se uvnitř dlaždice pozorování zobrazí chybová zpráva
   - **Řešení**: Zpráva pochází přímo ze služby zpracování satelitních snímků — přečtěte si ji pro konkrétní pokyny (např. neplatná konfigurace, chybějící data, příliš velká oblast)
   - Pokud je zpráva nejasná nebo přetrvává, kontaktujte administrátora s přesným textem, který se zobrazuje

---

#### Problém: FOI není viditelný

**Příznaky**:

- FOI se nezobrazuje na mapě ani v seznamu
- Vyhledávání nevrací žádné výsledky

**Možné příčiny a řešení**:

1. **Oprávnění rozsahu**:
   - **Řešení**: Ověřte, zda máte přístup ke správnému rozsahu
   - Kontaktujte administrátora se žádostí o oprávnění k rozsahu
   - Přepněte na rozsah, ke kterému máte přístup

2. **Příliš malé přiblížení mapy**:
   - **Řešení**: Přibližte mapu
   - FOI se nemusí zobrazovat, dokud nedosáhnete prahové úrovně přiblížení (obvykle úroveň přiblížení 12)

3. **Aktivní filtry**:
   - **Řešení**: Zrušte všechny aktivní filtry v **Seznamu zákresů JŽ**
   - Resetujte vyhledávací kritéria
   - Zkontrolujte, zda filtry váš FOI nevylučují

4. **Nesprávné ID zákresu JŽ**:
   - **Řešení**: Ověřte správnost ID zákresu JŽ
   - Použijte úplný formát referenčního ID (např. DEMO.FOI.6999931001)

5. **Vrstva není zobrazena**:
   - Vrstva může být vypnutá ve výběru vrstev mapy
   - **Řešení**: Otevřete **výběr vrstev mapy** a ujistěte se, že vrstva příslušného typu zákresu JŽ je zapnutá

---

#### Problém: Vypršení časového limitu ověření e-mailu

**Příznaky**:

- Ověřovací e-mail nebyl doručen
- Platnost odkazu vypršela (časový limit 5 minut)

**Možné příčiny a řešení**:

1. **Vypršelo 5minutové okno**:
   - **Řešení**: Restartujte proces přihlášení
   - Vyžádejte si nový ověřovací e-mail
   - Zkontrolujte e-mail okamžitě a klikněte na odkaz do 5 minut

2. **E-mail ve složce spam nebo nevyžádaná pošta**:
   - **Řešení**: Zkontrolujte složku spam nebo nevyžádaná pošta
   - Hledejte e-mail od noreply@planet.com
   - Označte jako „Není spam", aby vám budoucí e-maily chodily do doručené pošty

3. **Nesprávná e-mailová adresa**:
   - **Řešení**: Ověřte, zda je vaše e-mailová adresa v účtu Planet správná
   - Aktualizujte e-mailovou adresu a zkuste to znovu

---

#### Problém: Signály/indikátory se nezobrazují

**Příznaky**:

- Graf vizualizace signálů a indikátorů je prázdný
- Nezobrazují se žádné čáry ani indikátory

**Možné příčiny a řešení**:

1. **Signály/indikátory jsou skryté v legendě**:
   - **Řešení**: Kliknutím na položky legendy zapněte jejich viditelnost
   - Hledejte barevné čtverce (aktivní) oproti šedým (neaktivní)
   - Povolte signály/indikátory, které chcete zobrazit

2. **Kontext indikátorů není vybrán**:
   - **Řešení**: Otevřete **boční panel** a přejděte do **Nastavení**, poté v sekci **Indikátory** vyberte kontext indikátorů
   - Ujistěte se, že kontext má pro dané časové období vypočítané indikátory

3. **Problém s časovým obdobím**:
   - **Řešení**: Otevřete **boční panel** a přejděte do **Nastavení**, kde upravte časové období tak, aby zahrnovalo období se signálovými daty
   - Signály jsou dostupné pouze pro data, ke kterým existují satelitní pozorování

---

#### Problém: Stav vytvoření sady je FAILED

**Příznaky**:

- Sada ve **Validační konzoli** zobrazuje stav **FAILED** ihned po vytvoření
- Nejsou přiřazeny žádné balíčky ani zákresy JŽ

**Možné příčiny a řešení**:

1. **Filtry neodpovídají žádnému FOI**:
   - Pokud dotaz, seznam FOI ve formátu CSV nebo seznam referenčních ID ve formátu CSV nevrátí žádné platné zákresy JŽ, vytvoření sady selže.
   - **Řešení**: Zkontrolujte každý vstup filtru:
     - **Dotaz**: rozšiřte nebo opravte podmínky dotazu.
     - **Seznam FOI ve formátu CSV**: ověřte, zda jsou ID platná a patří do aktuálního rozsahu.
     - **Seznam referenčních ID ve formátu CSV**: ověřte, zda referenční ID existují a odpovídají vybranému typu zákresu JŽ.
   - Po provedení úprav vytvořte novou sadu — sady ve stavu FAILED nelze opakovat přímo.

2. **Dotaz je příliš široký (opačný problém)**:
   - Dotaz odpovídající desetitisícům zákresů JŽ může zvýšit počet balíčků nad 1000, i při rozumné velikosti balíčku.
   - **Řešení**: Zužte dotaz, aby celkový počet zákresů JŽ zůstal v rozsahu, kde počet balíčků nepřekročí limit.

---

#### Problém: Základní vrstva mapy se nenačítá nebo má nesprávný název

Pokud se základní vrstva nenačte nebo její název vypadá nesprávně či zastarale, konfigurace vrstvy je spravována na úrovni rozsahu — jednotliví uživatelé ji nemohou ovládat. Nahlaste problém **administrátoru rozsahu** a uveďte název vrstvy, který vidíte ve výběru vrstev mapy.

## Osvědčené postupy

#### Vždy synchronizujte časové období s dostupnými daty

- Před analýzou zákresu JŽ zkontrolujte, pro která časová období jsou dostupná data
- Nastavte časové období tak, aby odpovídalo vegetační sezoně nebo monitorovacímu období
- Nepředpokládejte, že data jsou dostupná pro všechna data

#### Používejte vhodnou vrstvu pro potřeby analýzy

- **Přirozené barvy** (True Color): pro přirozený vzhled a vizuální interpretaci
- **Falešné barvy** (False Color): pro analýzu hustoty vegetace
